import { IconButton, Stack } from '@mui/material';
import { FilterKeys, GetAssessmentsQueryFilters, RepetitionType } from '@schooly/api';
import { CrossIcon, TagSelect } from '@schooly/style';
import React, { FC, ReactNode, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useSchool } from '../../../../hooks/useSchool';
import useSchoolYears from '../../../../hooks/useSchoolYears';

type InlineFiltersTagsProps = {
  onOpenFilters: () => void;
  onResetFilters?: () => void;
  maxAmount?: number;
  filters: GetAssessmentsQueryFilters;
};

export const InlineFiltersTags: FC<InlineFiltersTagsProps> = ({
  filters,
  onOpenFilters,
  onResetFilters,
  maxAmount = 2,
}) => {
  const { schoolId } = useSchool();
  const { formatMessage } = useIntl();
  const { schoolYears } = useSchoolYears();
  const tags = useMemo(() => {
    const tagComponents: ReactNode[] = [];

    if (!schoolId) return tagComponents;

    const commonTagsProps = {
      size: 'small',
      onClick: onOpenFilters,
      sx: { maxWidth: 180 },
    } as const;

    if (filters[FilterKeys.Date]) {
      const schoolYear = schoolYears.find(
        (y) => y.start === filters[FilterKeys.Date]?.[0] && y.end === filters[FilterKeys.Date]?.[1],
      );

      if (schoolYear) {
        tagComponents.push(<TagSelect label={schoolYear.name} {...commonTagsProps} />);
      }
    }

    if (filters[FilterKeys.RepetitionType]?.includes(RepetitionType.FirstOfRecurrence)) {
      tagComponents.push(
        <TagSelect
          key={RepetitionType.FirstOfRecurrence}
          label={formatMessage(
            { id: 'recurring-showRecurringEntity' },
            { entityName: formatMessage({ id: 'assessments-Assessments' }) },
          )}
          {...commonTagsProps}
        />,
      );
    }

    if (tagComponents.length > maxAmount) {
      return [
        ...tagComponents.slice(0, maxAmount),
        <TagSelect
          key="more"
          label={`+${tagComponents.length - maxAmount}`}
          {...commonTagsProps}
        />,
      ];
    }

    return tagComponents;
  }, [schoolId, onOpenFilters, filters, maxAmount, schoolYears, formatMessage]);

  if (!tags.length) return null;

  return (
    <Stack flexDirection="row" flexWrap="wrap" gap={0.5}>
      {tags}{' '}
      {onResetFilters && (
        <IconButton
          onClick={onResetFilters}
          sx={(theme) => ({
            color: theme.palette.common.grey,
            '&:hover': { color: theme.palette.text.primary },
          })}
        >
          <CrossIcon />
        </IconButton>
      )}
    </Stack>
  );
};
